import React, { useState, useEffect, useContext } from 'react';
import { ThemeContext } from "../../../../../context/ThemeContext";
import { postData, serverURL } from '../../../../../services/Fetchnodeservices';

// ===================== ICONS =====================
const SearchIcon = () => (
  <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
    <circle cx="11" cy="11" r="8" />
    <line x1="21" y1="21" x2="16.65" y2="16.65" />
  </svg>
);

const BankIcon = () => (
  <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5">
    <polygon points="12 2 2 7 22 7" />
    <line x1="2" y1="22" x2="22" y2="22" />
    <line x1="4" y1="7" x2="4" y2="22" />
    <line x1="20" y1="7" x2="20" y2="22" />
    <line x1="8" y1="7" x2="8" y2="22" />
    <line x1="12" y1="7" x2="12" y2="22" />
    <line x1="16" y1="7" x2="16" y2="22" />
  </svg>
);

const WalletIcon = () => (
  <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5">
    <path d="M22 6v12a2 2 0 0 1-2 2H4a2 2 0 0 1-2-2V6a2 2 0 0 1 2-2h16a2 2 0 0 1 2 2z" />
    <path d="M22 10h-4a2 2 0 0 0-2 2v0a2 2 0 0 0 2 2h4" />
    <circle cx="18" cy="14" r="1" fill="currentColor" />
  </svg>
);

const FileIcon = () => (
  <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8">
    <path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z" />
    <polyline points="14 2 14 8 20 8" />
    <line x1="16" y1="13" x2="8" y2="13" />
    <line x1="16" y1="17" x2="8" y2="17" />
  </svg>
);

const EyeIcon = () => (
  <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8">
    <path d="M1 12s4-8 11-8 11 8 11 8-4 8-11 8-11-8-11-8z" />
    <circle cx="12" cy="12" r="3" />
  </svg>
);

const UserIcon = () => (
  <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
    <path d="M20 21v-2a4 4 0 0 0-4-4H8a4 4 0 0 0-4 4v2" />
    <circle cx="12" cy="7" r="4" />
  </svg>
);

const XIcon = () => (
  <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5">
    <line x1="18" y1="6" x2="6" y2="18" />
    <line x1="6" y1="6" x2="18" y2="18" />
  </svg>
);

const TrendingIcon = () => (
  <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
    <polyline points="23 6 13.5 15.5 8.5 10.5 1 18" />
    <polyline points="17 6 23 6 23 12" />
  </svg>
);

const CheckCircleIcon = () => (
  <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
    <path d="M22 11.08V12a10 10 0 1 1-5.93-9.14" />
    <polyline points="22 4 12 14.01 9 11.01" />
  </svg>
);

// ===================== STAT CARD =====================
const StatCard = ({ icon, title, value, color, theme }) => (
  <div
    style={{
      background: theme.surface,
      padding: 20,
      borderRadius: 16,
      boxShadow: `0 4px 12px ${theme.primary}10`,
      border: `1px solid ${theme.border}`,
      transition: 'transform 0.2s ease, box-shadow 0.2s ease',
    }}
    onMouseEnter={(e) => {
      e.currentTarget.style.transform = 'translateY(-2px)';
      e.currentTarget.style.boxShadow = `0 8px 25px ${theme.primary}20`;
    }}
    onMouseLeave={(e) => {
      e.currentTarget.style.transform = 'translateY(0)';
      e.currentTarget.style.boxShadow = `0 4px 12px ${theme.primary}10`;
    }}
  >
    <div style={{ display: 'flex', alignItems: 'center', gap: 12, marginBottom: 12 }}>
      <div
        style={{
          width: 44,
          height: 44,
          borderRadius: 12,
          background: `${color}1a`,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          color: color,
        }}
      >
        {icon}
      </div>
      <h4 style={{ margin: 0, fontSize: 12, color: theme.textSecondary, fontWeight: 500 }}>
        {title}
      </h4>
    </div>
    <h2 style={{ margin: 0, fontSize: 22, fontWeight: 700, color: theme.textPrimary }}>
      {value}
    </h2>
  </div>
);

// ===================== MAIN COMPONENT =====================
export default function BankDepositHistory() {
  const { theme } = useContext(ThemeContext); // ← Theme Context

  const [history, setHistory] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');

  // Modal States for Image Preview
  const [selectedImage, setSelectedImage] = useState(null);
  const [showModal, setShowModal] = useState(false);

  useEffect(() => {
    fetchHistory();
  }, []);

  const fetchHistory = async () => {
    setLoading(true);
    try {
      const res = await postData("vandersuperadmin/admin/cash/bank-deposit-history", {});
      if (res.success) setHistory(res.data || []);
    } catch (error) {
      console.error("Fetch History Error:", error);
    }
    setLoading(false);
  };

  const handleOpenImage = (imgName) => {
    setSelectedImage(imgName);
    setShowModal(true);
  };

  const filteredHistory = history.filter(item =>
    item.bank_name?.toLowerCase().includes(searchTerm.toLowerCase()) ||
    item.deposit_number?.toLowerCase().includes(searchTerm.toLowerCase()) ||
    item.staff_1_name?.toLowerCase().includes(searchTerm.toLowerCase()) ||
    String(item.id).includes(searchTerm)
  );

  // Stats
  const totalDeposited = history.reduce((sum, h) => sum + Number(h.total_amount || 0), 0);
  const totalTrips = history.length;
  const uniqueBanks = [...new Set(history.map(h => h.bank_name).filter(Boolean))].length;

  return (
    <div style={{ padding: 24, minHeight: '100vh', background: theme.background, fontFamily: "'DM Sans', sans-serif", transition: 'background 0.3s ease' }}>
      <style>{`
        @keyframes spin { to { transform: rotate(360deg); } }
        @keyframes fadeIn { from { opacity: 0; } to { opacity: 1; } }
        @keyframes zoomIn { from { transform: scale(0.9); opacity: 0; } to { transform: scale(1); opacity: 1; } }
      `}</style>

      {/* Header */}
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 24, flexWrap: 'wrap', gap: 16 }}>
        <div>
          <h2 style={{ margin: 0, fontSize: 22, fontWeight: 700, color: theme.textPrimary, display: 'flex', alignItems: 'center', gap: 10 }}>
            🏦 Bank Deposit History
          </h2>
          <p style={{ color: theme.textMuted, margin: '4px 0 0', fontSize: 13 }}>
            Owner View: Track all money deposited into bank accounts.
          </p>
        </div>

        {/* Search Input */}
        <div
          style={{
            display: 'flex',
            alignItems: 'center',
            gap: 10,
            padding: '10px 14px',
            background: theme.surface,
            border: `1px solid ${theme.border}`,
            borderRadius: 10,
            width: 300,
            transition: 'border-color 0.2s ease',
          }}
        >
          <SearchIcon />
          <input
            type="text"
            placeholder="Search by Bank, Staff or Trip ID..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            style={{
              flex: 1,
              padding: 0,
              border: 'none',
              outline: 'none',
              background: 'transparent',
              color: theme.textPrimary,
              fontSize: 13,
            }}
          />
          {searchTerm && (
            <button
              onClick={() => setSearchTerm('')}
              style={{
                background: 'none',
                border: 'none',
                cursor: 'pointer',
                color: theme.textMuted,
                padding: 2,
                display: 'flex',
                alignItems: 'center',
              }}
            >
              <XIcon />
            </button>
          )}
        </div>
      </div>

      {/* Stats Cards */}
      <div
        style={{
          display: "grid",
          gridTemplateColumns: "repeat(auto-fit, minmax(220px, 1fr))",
          gap: 16,
          marginBottom: 24,
        }}
      >
        <StatCard
          icon={<WalletIcon />}
          title="Total Deposited"
          value={`₹ ${totalDeposited.toLocaleString()}`}
          color={theme.success}
          theme={theme}
        />
        <StatCard
          icon={<TrendingIcon />}
          title="Total Trips"
          value={totalTrips}
          color={theme.primary}
          theme={theme}
        />
        <StatCard
          icon={<BankIcon />}
          title="Banks Used"
          value={uniqueBanks}
          color={theme.warning}
          theme={theme}
        />
      </div>

      {/* Content */}
      {loading ? (
        <div
          style={{
            textAlign: 'center',
            padding: 60,
            background: theme.surface,
            borderRadius: 16,
            border: `1px solid ${theme.border}`,
          }}
        >
          <div
            style={{
              width: 40,
              height: 40,
              border: `3px solid ${theme.border}`,
              borderTopColor: theme.primary,
              borderRadius: '50%',
              margin: '0 auto 16px',
              animation: 'spin 1s linear infinite',
            }}
          />
          <p style={{ color: theme.textSecondary, fontSize: 14 }}>
            Loading deposit history...
          </p>
        </div>
      ) : filteredHistory.length === 0 ? (
        <div
          style={{
            textAlign: 'center',
            padding: '80px 20px',
            background: theme.surface,
            border: `1px dashed ${theme.border}`,
            borderRadius: 16,
          }}
        >
          <div
            style={{
              width: 80,
              height: 80,
              borderRadius: '50%',
              background: `${theme.primary}1a`,
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              margin: '0 auto 16px',
              color: theme.primary,
            }}
          >
            <BankIcon />
          </div>
          <h3 style={{ color: theme.textPrimary, marginBottom: 8, fontSize: 18, fontWeight: 600 }}>
            {searchTerm ? 'No Matching Deposits' : 'No Deposit History'}
          </h3>
          <p style={{ color: theme.textMuted, fontSize: 13, margin: 0 }}>
            {searchTerm ? 'Try a different search term' : 'No bank deposits have been made yet'}
          </p>
        </div>
      ) : (
        <div
          style={{
            background: theme.surface,
            border: `1px solid ${theme.border}`,
            borderRadius: 16,
            overflow: 'hidden',
            boxShadow: `0 4px 12px ${theme.primary}08`,
          }}
        >
          <div style={{ overflowX: 'auto' }}>
            <table style={{ width: '100%', borderCollapse: 'collapse' }}>
              <thead>
                <tr style={{ background: theme.surfaceLighter, borderBottom: `2px solid ${theme.border}` }}>
                  <th style={thStyle(theme)}>Trip ID</th>
                  <th style={thStyle(theme)}>Verification Date</th>
                  <th style={thStyle(theme)}>Amount</th>
                  <th style={thStyle(theme)}>Bank & A/C</th>
                  <th style={thStyle(theme)}>Staff (Handover)</th>
                  <th style={thStyle(theme)}>Slip Info</th>
                  <th style={thStyle(theme)}>Verified By</th>
                </tr>
              </thead>
              <tbody>
                {filteredHistory.map(h => (
                  <tr
                    key={h.id}
                    style={{
                      borderBottom: `1px solid ${theme.border}`,
                      transition: 'background 0.2s ease',
                    }}
                    onMouseEnter={(e) =>
                      (e.currentTarget.style.background = theme.surfaceHover || `${theme.primary}08`)
                    }
                    onMouseLeave={(e) =>
                      (e.currentTarget.style.background = 'transparent')
                    }
                  >
                    <td style={{ ...tdStyle(theme), color: theme.primary, fontFamily: 'monospace', fontWeight: 700 }}>
                      #{h.id}
                    </td>
                    <td style={{ ...tdStyle(theme), color: theme.textSecondary, fontSize: 12 }}>
                      {new Date(h.verified_at).toLocaleString()}
                    </td>
                    <td style={{ ...tdStyle(theme), color: theme.success, fontWeight: 700, fontSize: 14 }}>
                      ₹{Number(h.total_amount || 0).toLocaleString()}
                    </td>
                    <td style={tdStyle(theme)}>
                      <div style={{ color: theme.textPrimary, fontWeight: 600, fontSize: 13 }}>
                        {h.bank_name || '-'}
                      </div>
                      <div style={{ fontSize: 11, color: theme.textMuted, marginTop: 2 }}>
                        {h.account_number || 'N/A'}
                      </div>
                    </td>
                    <td style={tdStyle(theme)}>
                      <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
                        <UserIcon />
                        <span style={{ color: theme.textPrimary, fontSize: 13 }}>
                          {h.staff_1_name || '-'}
                          {h.staff_2_name && (
                            <span style={{ color: theme.textMuted }}> & {h.staff_2_name}</span>
                          )}
                        </span>
                      </div>
                    </td>
                    <td style={tdStyle(theme)}>
                      <div style={{ display: 'flex', alignItems: 'center', gap: 6, marginBottom: 4 }}>
                        <FileIcon />
                        <span style={{ fontSize: 12, color: theme.textSecondary }}>
                          <strong>Ref:</strong> {h.deposit_number || '-'}
                        </span>
                      </div>
                      {h.slip_image && (
                        <button
                          onClick={() => handleOpenImage(h.slip_image)}
                          style={{
                            background: `${theme.primary}1a`,
                            border: 'none',
                            color: theme.primary,
                            padding: '4px 10px',
                            cursor: 'pointer',
                            fontSize: 11,
                            fontWeight: 600,
                            borderRadius: 6,
                            display: 'inline-flex',
                            alignItems: 'center',
                            gap: 4,
                            transition: 'all 0.2s ease',
                          }}
                          onMouseEnter={(e) => {
                            e.currentTarget.style.background = `${theme.primary}2a`;
                          }}
                          onMouseLeave={(e) => {
                            e.currentTarget.style.background = `${theme.primary}1a`;
                          }}
                        >
                          <EyeIcon /> View Slip
                        </button>
                      )}
                    </td>
                    <td style={tdStyle(theme)}>
                      <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
                        <CheckCircleIcon />
                        <span style={{ color: theme.textPrimary, fontSize: 13, fontWeight: 500 }}>
                          {h.verified_by_name || '-'}
                        </span>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>

          {/* Footer */}
          <div
            style={{
              padding: '14px 20px',
              borderTop: `1px solid ${theme.border}`,
              background: theme.surfaceLighter,
              display: 'flex',
              justifyContent: 'space-between',
              alignItems: 'center',
              flexWrap: 'wrap',
              gap: 12,
            }}
          >
            <span style={{ fontSize: 12, color: theme.textSecondary }}>
              Showing <strong style={{ color: theme.textPrimary }}>{filteredHistory.length}</strong> deposit{filteredHistory.length !== 1 ? 's' : ''}
            </span>
            <span style={{ fontSize: 12, color: theme.success, fontWeight: 600 }}>
              Total: ₹ {totalDeposited.toLocaleString()}
            </span>
          </div>
        </div>
      )}

      {/* --- IMAGE PREVIEW POPUP (MODAL) --- */}
      {showModal && selectedImage && (
        <div
          style={{
            position: 'fixed',
            top: 0,
            left: 0,
            width: '100%',
            height: '100%',
            background: 'rgba(0,0,0,0.85)',
            backdropFilter: 'blur(6px)',
            display: 'flex',
            justifyContent: 'center',
            alignItems: 'center',
            zIndex: 9999,
            flexDirection: 'column',
            animation: 'fadeIn 0.2s ease',
          }}
          onClick={() => setShowModal(false)}
        >
          <div
            style={{
              position: 'relative',
              maxWidth: '85%',
              maxHeight: '85%',
              animation: 'zoomIn 0.3s ease',
            }}
            onClick={(e) => e.stopPropagation()}
          >
            {/* Close Button */}
            <button
              onClick={() => setShowModal(false)}
              style={{
                position: 'absolute',
                top: -16,
                right: -16,
                background: theme.error || theme.danger,
                border: 'none',
                borderRadius: '50%',
                width: 36,
                height: 36,
                cursor: 'pointer',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                color: '#fff',
                boxShadow: '0 4px 12px rgba(0,0,0,0.3)',
                zIndex: 10,
                transition: 'transform 0.2s ease',
              }}
              onMouseEnter={(e) => {
                e.currentTarget.style.transform = 'scale(1.1)';
              }}
              onMouseLeave={(e) => {
                e.currentTarget.style.transform = 'scale(1)';
              }}
            >
              <XIcon />
            </button>

            {/* Image */}
            <img
              src={`${serverURL}/uploads/${selectedImage}`}
              alt="Bank Slip"
              style={{
                width: '100%',
                maxHeight: '70vh',
                objectFit: 'contain',
                borderRadius: 12,
                boxShadow: `0 20px 50px ${theme.primary}30`,
              }}
              onError={(e) => {
                e.target.onerror = null;
                e.target.src = "https://via.placeholder.com/500x700?text=Slip+Not+Found";
              }}
            />
          </div>

          {/* Caption */}
          <div
            style={{
              color: '#fff',
              marginTop: 20,
              background: 'rgba(255,255,255,0.15)',
              padding: '10px 25px',
              borderRadius: 30,
              fontSize: 14,
              fontWeight: 500,
              backdropFilter: 'blur(4px)',
              display: 'flex',
              alignItems: 'center',
              gap: 8,
            }}
          >
            <FileIcon /> Bank Deposit Slip Photo
          </div>
        </div>
      )}
    </div>
  );
}

// ===================== TABLE STYLES =====================
const thStyle = (theme) => ({
  padding: "14px 16px",
  textAlign: "left",
  fontSize: 11,
  fontWeight: 700,
  color: theme.primary,
  whiteSpace: "nowrap",
});

const tdStyle = (theme) => ({
  padding: "14px 16px",
  fontSize: 13,
  color: theme.textPrimary,
});